import { ArrowDown, ArrowUp, FolderClosed, FolderInput, FolderUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import * as Context from '@/components/ui/context-menu';
import * as Dropdown from '@/components/ui/dropdown-menu';
import { canMoveLayer } from './layerTreeMoves';
import type { LayerOrdering } from './useLayerOrdering';

export function LayerMoveActions({ layerId, ordering, groupByType, dropdown = false }: {
  layerId: string; ordering: LayerOrdering; groupByType: boolean; dropdown?: boolean;
}) {
  const { t } = useTranslation('project');
  const Item = dropdown ? Dropdown.DropdownMenuItem : Context.ContextMenuItem;
  const Sub = dropdown ? Dropdown.DropdownMenuSub : Context.ContextMenuSub;
  const SubTrigger = dropdown ? Dropdown.DropdownMenuSubTrigger : Context.ContextMenuSubTrigger;
  const SubContent = dropdown ? Dropdown.DropdownMenuSubContent : Context.ContextMenuSubContent;
  const Portal = dropdown ? Dropdown.DropdownMenuPortal : Context.ContextMenuPortal;
  const source = ordering.items.find((item) => item.layer.id === layerId);
  if (!source) return null;
  const peers = ordering.siblings(source.parentId).filter((item) =>
    !groupByType || source.parentId !== null || item.layer.type === source.layer.type);
  const index = peers.indexOf(source);
  const folders = ordering.items.filter((item) => item.layer.type === 'GROUP' && item.layer.id !== source.parentId &&
    canMoveLayer(ordering.items, layerId, item.layer.id));
  return <>
    <Item disabled={index <= 0 || ordering.isPending} onSelect={() => ordering.moveBy(layerId, -1, groupByType)}>
      <ArrowUp className='size-4' />{t('viewerLayers.moveEarlier')}
    </Item>
    <Item disabled={index === peers.length - 1 || ordering.isPending} onSelect={() => ordering.moveBy(layerId, 1, groupByType)}>
      <ArrowDown className='size-4' />{t('viewerLayers.moveLater')}
    </Item>
    {source.parentId && <Item disabled={ordering.isPending} onSelect={() => ordering.moveToFolder(layerId, null)}>
      <FolderUp className='size-4' />{t('viewerLayers.moveToRoot')}
    </Item>}
    {folders.length > 0 && <Sub>
      <SubTrigger disabled={ordering.isPending} className='gap-2'>
        <FolderInput className='size-4' />{t('viewerLayers.moveToFolder')}
      </SubTrigger>
      <Portal><SubContent className='max-h-80 min-w-48 max-w-72 overflow-y-auto'>
        {folders.map(({ layer }) => <Item key={layer.id} onSelect={() => ordering.moveToFolder(layerId, layer.id)}>
          <FolderClosed className='size-4' /><span className='truncate' title={layer.name}>{layer.name}</span>
        </Item>)}
      </SubContent></Portal>
    </Sub>}
  </>;
}
